<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb
            :title="page.title"
            :breadcrumbs="breadcrumbs"
         ></BaseBreadcrumb>

         <v-alert dense text type="info" class="cpd-intro">
            Register your attended CPD programme here. Submitted records will be reviewed by the Secretariat before points are counted.
         </v-alert>

         <v-row dense>
            <v-col cols="12" md="4">
               <v-card outlined class="member-cpd-summary approved-summary">
                  <v-card-text>
                     <div class="caption grey--text text--darken-1">Approved CPD Points</div>
                     <div class="display-1 font-weight-bold">{{ totalApprovedPoints }}</div>
                     <div class="caption grey--text">Total approved points for your records.</div>
                  </v-card-text>
               </v-card>
            </v-col>
            <v-col cols="12" md="4">
               <v-card outlined class="member-cpd-summary pending-summary">
                  <v-card-text>
                     <div class="caption grey--text text--darken-1">Pending Registrations</div>
                     <div class="display-1 font-weight-bold">{{ pendingItems.length }}</div>
                     <div class="caption grey--text">Waiting for admin approval.</div>
                  </v-card-text>
               </v-card>
            </v-col>
            <v-col cols="12" md="4">
               <v-card outlined class="member-cpd-summary year-summary">
                  <v-card-text>
                     <v-text-field
                        v-model="selectedYear"
                        label="CPD Year"
                        type="number"
                        dense
                        outlined
                        hide-details
                        @change="loadAll"
                     ></v-text-field>
                  </v-card-text>
               </v-card>
            </v-col>
         </v-row>

         <v-card class="mt-4 cpd-register-card">
            <v-toolbar flat color="white" class="cpd-toolbar">
               <v-toolbar-title>
                  <v-icon left color="primary">mdi-calendar-plus</v-icon>
                  Register CPD Programme
               </v-toolbar-title>
               <v-divider class="mx-4" inset vertical></v-divider>
               <v-spacer></v-spacer>
               <v-btn color="primary" dark :loading="submitting" :disabled="!canSubmitExistingPrograms" @click="submitRegistration">
                  Submit Registration
               </v-btn>
            </v-toolbar>
            <v-card-text>
               <v-alert v-if="message" :type="messageType" dense outlined>
                  {{ message }}
               </v-alert>
               <v-autocomplete
                  v-model="selectedProgram"
                  :items="selectableAvailablePrograms"
                  item-text="epname"
                  item-value="id"
                  return-object
                  label="Search and select up to 3 available CPD programmes"
                  clearable
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  outlined
                  dense
                  :loading="loadingPrograms"
                  :rules="[selectedProgramsLimitRule]"
                  :no-data-text="selectedPrograms.length >= 3 ? 'Maximum 3 programmes selected' : 'No available programme for this year'"
                  @change="normalizeSelectedPrograms"
               >
                  <template v-slot:item="{ item }">
                     <v-list-item-content>
                        <v-list-item-title>{{ item.epname || '-' }}</v-list-item-title>
                        <v-list-item-subtitle>
                           {{ item.epcode || '-' }} · {{ item.epyear || selectedYear }} · {{ item.eporg || 'Organizer not set' }}
                        </v-list-item-subtitle>
                     </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item, index }">
                     <v-chip small close @click:close="removeSelectedProgram(index)">
                        {{ item.epname }}
                     </v-chip>
                  </template>
               </v-autocomplete>

               <v-alert v-if="selectedPrograms.length" dense text type="info" class="mb-3">
                  {{ selectedPrograms.length }} programme{{ selectedPrograms.length > 1 ? 's' : '' }} selected. You can submit a maximum of 3 programmes at a time.
               </v-alert>

               <v-card v-if="selectedPrograms.length" outlined class="selected-program-card">
                  <v-card-text>
                     <div v-for="program in selectedPrograms" :key="program.id" class="selected-program-row">
                        <div class="selected-program-title">{{ program.epname }}</div>
                        <div class="selected-program-meta">
                           <span>Code: {{ program.epcode || '-' }}</span>
                           <span>Year: {{ program.epyear || selectedYear }}</span>
                           <span>Points: {{ program.eppoints || program.cpdpoints || 0 }}</span>
                        </div>
                     </div>
                  </v-card-text>
               </v-card>
               <v-file-input
                  v-model="supportingdoc"
                  label="Upload Supporting Document (certificate / proof of attendance)"
                  accept=".pdf,.jpg,.jpeg,.png"
                  outlined
                  dense
                  show-size
                  prepend-icon="mdi-paperclip"
                  class="mt-3"
                  hint="PDF, JPG or PNG, max 20MB"
                  persistent-hint
                  :rules="[v => !!v || 'Supporting document is required']"
               >
               </v-file-input>

               <v-divider class="my-5"></v-divider>

               <v-expansion-panels v-model="manualPanel" flat class="unlisted-cpd-panel">
                  <v-expansion-panel>
                     <v-expansion-panel-header class="unlisted-cpd-header">
                        <div class="unlisted-cpd-heading">
                           <div class="unlisted-cpd-icon">
                              <v-icon color="primary">mdi-file-document-plus-outline</v-icon>
                           </div>
                           <div class="unlisted-cpd-copy">
                              <div class="unlisted-cpd-title">Programme not listed?</div>
                              <div class="unlisted-cpd-subtitle">
                                 Submit the programme details with supporting evidence for Secretariat review.
                              </div>
                           </div>
                           <v-chip small outlined color="primary" class="unlisted-cpd-chip">
                              Review required
                           </v-chip>
                        </div>
                     </v-expansion-panel-header>
                     <v-expansion-panel-content>
                        <v-form ref="manualCpdForm" v-model="manualValid">
                           <v-row dense>
                              <v-col cols="12" md="8">
                                 <v-text-field
                                    v-model="manualProgram.epname"
                                    label="Programme Title"
                                    outlined
                                    dense
                                    :rules="[requiredRule]"
                                 ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="4">
                                 <v-select
                                    v-model="manualProgram.epcatid"
                                    :items="cpdCategories"
                                    item-text="category"
                                    item-value="id"
                                    label="Category"
                                    outlined
                                    dense
                                    :rules="[requiredRule]"
                                 ></v-select>
                              </v-col>
                              <v-col cols="12" md="4">
                                 <v-text-field
                                    v-model="manualProgram.eporg"
                                    label="Organizer"
                                    outlined
                                    dense
                                    :rules="[requiredRule]"
                                 ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="4">
                                 <v-text-field
                                    v-model="manualProgram.epdate"
                                    label="Programme Date"
                                    type="date"
                                    outlined
                                    dense
                                    :rules="[requiredRule]"
                                 ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="4">
                                 <v-text-field
                                    v-model="manualProgram.epduration"
                                    label="Duration (hours)"
                                    type="number"
                                    min="1"
                                    outlined
                                    dense
                                    :rules="[requiredRule]"
                                 ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                 <v-text-field
                                    v-model="manualProgram.epemail"
                                    label="Organizer Email"
                                    outlined
                                    dense
                                 ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                 <v-text-field
                                    v-model="manualProgram.epcontactno"
                                    label="Organizer Contact No."
                                    outlined
                                    dense
                                 ></v-text-field>
                              </v-col>
                              <v-col cols="12">
                                 <v-file-input
                                    v-model="manualFile"
                                    label="Upload Supporting Document"
                                    prepend-icon="mdi-paperclip"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    show-size
                                    outlined
                                    dense
                                    :rules="[requiredRule]"
                                    hint="Upload certificate, attendance proof, poster, or official programme document."
                                    persistent-hint
                                 ></v-file-input>
                              </v-col>
                           </v-row>
                        </v-form>
                        <div class="d-flex justify-end mt-2">
                           <v-btn
                              color="primary"
                              dark
                              :loading="submittingManual"
                              :disabled="submittingManual"
                              @click="submitUnlistedProgram"
                           >
                              Submit Unlisted Programme
                           </v-btn>
                        </div>
                     </v-expansion-panel-content>
                  </v-expansion-panel>
               </v-expansion-panels>
            </v-card-text>
         </v-card>

         <v-card class="mt-4 cpd-record-card">
            <v-tabs v-model="tab" background-color="primary" dark>
               <v-tab>
                  Approved
                  <v-chip x-small color="green darken-2" dark class="ml-2">{{ approvedItems.length }}</v-chip>
               </v-tab>
               <v-tab>
                  Pending Review
                  <v-chip x-small color="orange darken-2" dark class="ml-2">{{ pendingItems.length }}</v-chip>
               </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
               <v-tab-item>
                  <v-data-table
                     :headers="headers"
                     :items="approvedItems"
                     :loading="loadingApproved"
                     class="border cpd-member-table"
                     no-data-text="No approved CPD records"
                  >
                     <template v-slot:[`item.cpdpoints`]="{ item }">
                        <v-chip color="green" small text-color="white">{{ item.cpdpoints || 0 }}</v-chip>
                     </template>
                     <template v-slot:[`item.reviewstatus`]>
                        <v-chip color="green darken-1" small text-color="white">
                           Approved
                        </v-chip>
                     </template>
                     <template v-slot:[`item.actions`]>
                        <span class="grey--text">-</span>
                     </template>
                  </v-data-table>
               </v-tab-item>
               <v-tab-item>
                  <v-data-table
                     :headers="headers"
                     :items="pendingItems"
                     :loading="loadingPending"
                     class="border cpd-member-table"
                     no-data-text="No pending CPD records"
                  >
                     <template v-slot:[`item.cpdpoints`]="{ item }">
                        <v-chip color="orange darken-2" small text-color="white">{{ item.cpdpoints || 0 }}</v-chip>
                     </template>
                     <template v-slot:[`item.epname`]="{ item }">
                        <div class="py-2">
                           <div class="font-weight-medium">{{ item.epname || '-' }}</div>
                           <v-alert
                              v-if="item.moreinfo_requested_at"
                              dense
                              text
                              type="warning"
                              class="mt-2 mb-0 cpd-more-info-alert"
                           >
                              {{ item.moreinfo_message || 'Please provide additional information for this CPD registration.' }}
                           </v-alert>
                        </div>
                     </template>
                     <template v-slot:[`item.reviewstatus`]="{ item }">
                        <v-chip
                           v-if="item.moreinfo_requested_at"
                           color="red darken-1"
                           small
                           text-color="white"
                        >
                           More Info Requested
                        </v-chip>
                        <v-chip v-else color="orange darken-2" small text-color="white">
                           Pending Review
                        </v-chip>
                     </template>
                     <template v-slot:[`item.actions`]="{ item }">
                        <v-btn
                           v-if="item.moreinfo_requested_at"
                           color="primary"
                           small
                           outlined
                           @click="openSupportingDocDialog(item)"
                        >
                           <v-icon left small>mdi-upload</v-icon>
                           Upload Document
                        </v-btn>
                        <span v-else class="grey--text">-</span>
                     </template>
                  </v-data-table>
               </v-tab-item>
            </v-tabs-items>
         </v-card>

         <v-dialog v-model="supportingDocDialog" max-width="560px">
            <v-card>
               <v-card-title>
                  Upload Supporting Document
                  <v-spacer></v-spacer>
                  <v-btn icon @click="supportingDocDialog = false">
                     <v-icon>mdi-close</v-icon>
                  </v-btn>
               </v-card-title>
               <v-card-text>
                  <v-alert dense text type="info">
                     Upload the additional or corrected supporting document requested by the Secretariat.
                  </v-alert>
                  <div v-if="supportingDocItem" class="mb-3">
                     <div class="caption grey--text text--darken-1">Programme</div>
                     <div class="font-weight-bold">{{ supportingDocItem.epname || '-' }}</div>
                  </div>
                  <v-file-input
                     v-model="supportingDocFile"
                     label="Supporting Document"
                     prepend-icon="mdi-paperclip"
                     accept=".pdf,.jpg,.jpeg,.png"
                     show-size
                     outlined
                     dense
                     hint="PDF, JPG or PNG, max 20MB"
                     persistent-hint
                  ></v-file-input>
               </v-card-text>
               <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="supportingDocDialog = false">Cancel</v-btn>
                  <v-btn
                     color="primary"
                     dark
                     :loading="uploadingSupportingDoc"
                     :disabled="!supportingDocFile || uploadingSupportingDoc"
                     @click="submitSupportingDoc"
                  >
                     Submit Document
                  </v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>
      </v-container>
   </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
const axios = require('axios')

export default {
   name: 'MOMCpd',
   components: {
      DashboardLayout,
      BaseBreadcrumb,
   },
   data() {
      return {
         page: { title: 'CPD' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'CPD', disabled: true },
         ],
         selectedYear: new Date().getFullYear(),
         tab: 0,
         approvedItems: [],
         pendingItems: [],
         availablePrograms: [],
         cpdCategories: [],
         selectedProgram: [],
         supportingdoc: null,
         manualPanel: null,
         manualValid: false,
         manualFile: null,
         supportingDocDialog: false,
         supportingDocItem: null,
         supportingDocFile: null,
         uploadingSupportingDoc: false,
         manualProgram: {
            epname: '',
            epcatid: null,
            eporg: '',
            epemail: '',
            epcontactno: '',
            epdate: '',
            epduration: '',
         },
         loadingApproved: false,
         loadingPending: false,
         loadingPrograms: false,
         submitting: false,
         submittingManual: false,
         message: '',
         messageType: 'info',
         headers: [
            { text: 'Year', value: 'cpdyear', width: '110' },
            { text: 'Code', value: 'epcode', width: '140' },
            { text: 'Programme', value: 'epname' },
            { text: 'Points', value: 'cpdpoints', width: '120' },
            { text: 'Status', value: 'reviewstatus', width: '190' },
            { text: 'Action', value: 'actions', width: '190', sortable: false },
         ],
      }
   },
   computed: {
      totalApprovedPoints() {
         return this.approvedItems.reduce((total, item) => total + parseInt(item.cpdpoints || 0), 0)
      },
      requiredRule() {
         return value => !!value || 'Required'
      },
      selectedPrograms() {
         return Array.isArray(this.selectedProgram) ? this.selectedProgram : (this.selectedProgram ? [this.selectedProgram] : [])
      },
      selectableAvailablePrograms() {
         if (this.selectedPrograms.length < 3) {
            return this.availablePrograms
         }

         const selectedIds = this.selectedPrograms.map(program => program && program.id)
         return this.availablePrograms.filter(program => selectedIds.includes(program.id))
      },
      selectedProgramsLimitRule() {
         return value => {
            const items = Array.isArray(value) ? value : (value ? [value] : [])
            return items.length <= 3 || 'You can select up to 3 programmes at a time.'
         }
      },
      canSubmitExistingPrograms() {
         return this.selectedPrograms.length > 0
            && this.selectedPrograms.length <= 3
            && !!this.supportingdoc
            && !this.submitting
      },
   },
   mounted() {
      this.loadAll()
      this.loadCpdCategories()
   },
   methods: {
      loadAll() {
         this.message = ''
         this.loadApproved()
         this.loadPending()
         this.loadAvailablePrograms()
      },

      loadCpdCategories() {
         axios.get('/member/cpd/evtprg-cat/list')
            .then((resp) => {
               this.cpdCategories = resp.data && resp.data.status === 'OK' ? (resp.data.data || []) : []
            })
            .catch(() => {
               this.cpdCategories = []
            })
      },

      loadApproved() {
         this.loadingApproved = true
         axios.get('/member/cpd/list-approved')
            .then((resp) => {
               this.approvedItems = resp.data && resp.data.status === 'OK' ? (resp.data.data || []) : []
            })
            .catch(() => {
               this.approvedItems = []
            })
            .finally(() => {
               this.loadingApproved = false
            })
      },

      loadPending() {
         this.loadingPending = true
         axios.get('/member/cpd/list-unapproved')
            .then((resp) => {
               this.pendingItems = resp.data && resp.data.status === 'OK' ? (resp.data.data || []) : []
            })
            .catch(() => {
               this.pendingItems = []
            })
            .finally(() => {
               this.loadingPending = false
            })
      },

      loadAvailablePrograms() {
         this.loadingPrograms = true
         this.selectedProgram = []
         axios.get('/member/cpd/evtprogs/' + this.selectedYear)
            .then((resp) => {
               this.availablePrograms = resp.data && resp.data.status === 'OK' ? (resp.data.data || []) : []
            })
            .catch(() => {
               this.availablePrograms = []
            })
            .finally(() => {
               this.loadingPrograms = false
            })
      },

      submitRegistration() {
         if (!this.selectedPrograms.length) {
            return
         }
         if (this.selectedPrograms.length > 3) {
            this.messageType = 'error'
            this.message = 'You can submit a maximum of 3 CPD programmes at a time.'
            return
         }
         if (!this.supportingdoc) {
            this.messageType = 'error'
            this.message = 'Please upload your supporting document (certificate / proof of attendance).'
            return
         }

         const cpdregs = this.selectedPrograms.map(program => ({
            cpdyear: parseInt(this.selectedYear),
            epitem: program,
         }))

         const data = new FormData()
         data.append('cpdregs', JSON.stringify(cpdregs))
         data.append('supportingdoc', this.supportingdoc)

         this.submitting = true
         this.message = ''
         axios.post('/member/cpd/registration/add', data)
            .then((resp) => {
               if (resp.data && resp.data.status === 'OK') {
                  this.messageType = 'success'
                  this.message = resp.data.message || 'CPD registration submitted.'
                  this.selectedProgram = []
                  this.supportingdoc = null
                  this.loadAll()
                  return
               }

               this.messageType = 'error'
               this.message = (resp.data && resp.data.error) ? resp.data.error : 'Unable to submit CPD registration.'
            })
            .catch((error) => {
               this.messageType = 'error'
               this.message = this.responseErrorMessage(error, 'Unable to submit CPD registration.')
            })
            .finally(() => {
               this.submitting = false
            })
      },
      responseErrorMessage(error, fallback) {
         var data = error && error.response ? error.response.data : null
         if (!data) return fallback
         if (data.error) return data.error
         if (data.message) return data.message
         if (data.errors) {
            var firstKey = Object.keys(data.errors)[0]
            if (firstKey && data.errors[firstKey] && data.errors[firstKey][0]) {
               return data.errors[firstKey][0]
            }
         }
         return fallback
      },
      removeSelectedProgram(index) {
         this.selectedProgram = this.selectedPrograms.filter((item, itemIndex) => itemIndex !== index)
      },
      normalizeSelectedPrograms(value) {
         const items = Array.isArray(value) ? value : (value ? [value] : [])
         if (items.length <= 3) {
            return
         }

         this.selectedProgram = items.slice(0, 3)
         this.messageType = 'info'
         this.message = 'Maximum 3 CPD programmes can be selected at a time. Remove one programme to choose another.'
      },

      resetManualProgram() {
         this.manualProgram = {
            epname: '',
            epcatid: null,
            eporg: '',
            epemail: '',
            epcontactno: '',
            epdate: '',
            epduration: '',
         }
         this.manualFile = null
         this.manualPanel = null
         if (this.$refs.manualCpdForm) {
            this.$refs.manualCpdForm.resetValidation()
         }
      },

      submitUnlistedProgram() {
         if (this.$refs.manualCpdForm && !this.$refs.manualCpdForm.validate()) {
            return
         }

         const data = new FormData()
         data.append('epyear', parseInt(this.selectedYear))
         data.append('epname', this.manualProgram.epname)
         data.append('epcatid', this.manualProgram.epcatid)
         data.append('eporg', this.manualProgram.eporg || '')
         data.append('epemail', this.manualProgram.epemail || '')
         data.append('epcontactno', this.manualProgram.epcontactno || '')
         data.append('epdate', this.manualProgram.epdate)
         data.append('epduration', this.manualProgram.epduration)
         if (this.manualFile) {
            data.append('epposter', this.manualFile)
         }

         this.submittingManual = true
         this.message = ''
         axios.post('/member/cpd/evtprog/add', data, {
            headers: {
               'Content-Type': 'multipart/form-data'
            }
         })
            .then((resp) => {
               if (!resp.data || resp.data.status !== 'OK' || !resp.data.data) {
                  this.messageType = 'error'
                  this.message = (resp.data && resp.data.error) ? resp.data.error : 'Unable to submit unlisted CPD programme.'
                  return Promise.reject(new Error(this.message))
               }

               const cpdregs = [{
                  cpdyear: parseInt(this.selectedYear),
                  epitem: resp.data.data,
               }]
               const registrationData = new FormData()
               registrationData.append('cpdregs', JSON.stringify(cpdregs))
               registrationData.append('supportingdoc', this.manualFile)

               return axios.post('/member/cpd/registration/add', registrationData)
            })
            .then((resp) => {
               if (resp.data && resp.data.status === 'OK') {
                  this.messageType = 'success'
                  this.message = 'Unlisted CPD programme submitted for Secretariat review.'
                  this.resetManualProgram()
                  this.loadAll()
                  return
               }

               this.messageType = 'error'
               this.message = (resp.data && resp.data.error) ? resp.data.error : 'Programme saved, but registration could not be submitted.'
            })
            .catch((error) => {
               if (!this.message) {
                  this.messageType = 'error'
                  this.message = error.response && error.response.data && error.response.data.error
                     ? error.response.data.error
                     : 'Unable to submit unlisted CPD programme.'
               }
            })
            .finally(() => {
               this.submittingManual = false
            })
      },
      openSupportingDocDialog(item) {
         this.supportingDocItem = item
         this.supportingDocFile = null
         this.supportingDocDialog = true
      },
      submitSupportingDoc() {
         if (!this.supportingDocItem || !this.supportingDocFile) {
            return
         }

         const data = new FormData()
         data.append('id', this.supportingDocItem.id)
         data.append('supportingdoc', this.supportingDocFile)

         this.uploadingSupportingDoc = true
         axios.post('/member/cpd/registration/supporting-document', data, {
            headers: {
               'Content-Type': 'multipart/form-data'
            }
         })
            .then((resp) => {
               if (resp.data && resp.data.status === 'OK') {
                  this.messageType = 'success'
                  this.message = resp.data.message || 'Supporting document updated.'
                  this.supportingDocDialog = false
                  this.supportingDocItem = null
                  this.supportingDocFile = null
                  this.loadAll()
                  return
               }

               this.messageType = 'error'
               this.message = (resp.data && resp.data.error) ? resp.data.error : 'Unable to update supporting document.'
            })
            .catch((error) => {
               this.messageType = 'error'
               this.message = error.response && error.response.data && error.response.data.error
                  ? error.response.data.error
                  : 'Unable to update supporting document.'
            })
            .finally(() => {
               this.uploadingSupportingDoc = false
            })
      },
   },
}
</script>

<style scoped>
.cpd-intro {
   border-radius: 8px;
}

.member-cpd-summary {
   border-left: 4px solid #90a4ae !important;
   border-radius: 8px;
   min-height: 122px;
}

.approved-summary {
   border-left-color: #2e7d32 !important;
}

.pending-summary {
   border-left-color: #ef6c00 !important;
}

.year-summary {
   border-left-color: #1976d2 !important;
}

.cpd-register-card,
.cpd-record-card {
   border-radius: 8px;
   overflow: hidden;
}

.cpd-toolbar ::v-deep .v-toolbar__title {
   align-items: center;
   display: flex;
   font-size: 18px;
   font-weight: 600;
}

.selected-program-card {
   background: #f8fafc;
   border-radius: 8px;
   margin-top: 8px;
}

.selected-program-row + .selected-program-row {
   border-top: 1px solid #e5e7eb;
   margin-top: 12px;
   padding-top: 12px;
}

.selected-program-title {
   color: #111827;
   font-size: 16px;
   font-weight: 700;
   line-height: 1.35;
}

.selected-program-meta {
   color: #6b7280;
   display: flex;
   flex-wrap: wrap;
   gap: 14px;
   font-size: 12px;
   font-weight: 600;
   margin-top: 8px;
}

.cpd-member-table ::v-deep thead th {
   color: #374151 !important;
   font-size: 12px;
   font-weight: 700;
}

.cpd-member-table ::v-deep tbody td {
   color: #111827;
   font-size: 13px;
}

.cpd-more-info-alert {
   max-width: 680px;
}

.unlisted-cpd-panel {
   border: 1px solid #e5e7eb;
   border-radius: 8px;
   box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
   overflow: hidden;
}

.unlisted-cpd-header {
   background: linear-gradient(90deg, #f8fbff 0%, #eef6ff 100%);
   min-height: 76px;
}

.unlisted-cpd-heading {
   align-items: center;
   display: flex;
   gap: 14px;
   min-width: 0;
   width: 100%;
}

.unlisted-cpd-icon {
   align-items: center;
   background: #ffffff;
   border: 1px solid #dbeafe;
   border-radius: 8px;
   display: flex;
   flex: 0 0 auto;
   height: 42px;
   justify-content: center;
   width: 42px;
}

.unlisted-cpd-copy {
   flex: 1 1 auto;
   min-width: 0;
}

.unlisted-cpd-title {
   color: #0f172a;
   font-size: 15px;
   font-weight: 700;
   line-height: 1.3;
}

.unlisted-cpd-subtitle {
   color: #64748b;
   font-size: 12px;
   line-height: 1.4;
   margin-top: 3px;
}

.unlisted-cpd-chip {
   flex: 0 0 auto;
   font-weight: 700;
   letter-spacing: 0;
}

@media (max-width: 600px) {
   .unlisted-cpd-heading {
      align-items: flex-start;
      flex-wrap: wrap;
   }

   .unlisted-cpd-chip {
      margin-left: 56px;
   }
}
</style>
